import { getCurrentUser } from '@Shared/api/identity';
import { getVersion } from '@Shared/api/version';
import { AuthToken } from '@Shared/types/auth';
import { PortalSettings } from '@Shared/types/portal/PortalSettings';
import { CurrentUserDetail } from '@Shared/types/portal/UserInfo';
import { formUrlEncodingSerialization, trackAppInsightsEvent, useAppInsights, wrapFallible } from '@Shared/utils/util';
import type { ICustomProperties } from '@microsoft/applicationinsights-web';
import axios from 'axios';
import { defineStore } from 'pinia';
import * as tau from 'tau-time';
import { useToast } from 'vue-toastification';
import { getPortalSettings } from '@Shared/api/settings';
import { Permission } from '@Shared/types/Permission';
import { ContentProfile } from '@Shared/types/portal/ContentItem';
import { getContentProfiles } from '@Shared/api/content';
import { DataViewerProfile } from '@Shared/types/portal/DataViewerProfile';
import { v4 as uuid } from 'uuid';

if (!import.meta.env.VITE_BASE_AUTHSERVER_URL) {
  console.error('VITE_BASE_AUTHSERVER_URL has to be defined in .env config and pointing to AuthServer');
}

export const useRootStore = defineStore('root', {
  state: (): {
    authServerUri: string | null;
    portalUri: string | null;
    portalSettings: PortalSettings | null;
    clientId: string | null;
    appName: string | null;
    environment: string | null;
    frontendVersion: string | null;
    moduleType: string | null;
    token: AuthToken | null;
    currentUser: CurrentUserDetail | null;
    moduleId: string | null;
    contentProfile: string | null;
    eventsSent: { [key: string]: string };
    currentOutstandingRequests: number;
    jsonDemoData: {
      profiles?: DataViewerProfile[];
      filters?: Record<string, object>;
      data?: Record<string, object>;
    } | null;
    contentProfiles: ContentProfile[];
  } => ({
    authServerUri: import.meta.env.VITE_BASE_AUTHSERVER_URL,
    portalUri: null,
    portalSettings: null,
    clientId: null,
    appName: null,
    moduleType: null,
    environment: null,
    frontendVersion: null,
    token: null,
    currentUser: null,
    moduleId: null,
    contentProfile: null,
    eventsSent: {},
    currentOutstandingRequests: 0,
    jsonDemoData: null,
    contentProfiles: [],
  }),
  actions: {
    async initializePortal() {
      this.portalUri = location.origin;

      try {
        this.portalSettings = await getPortalSettings();
        this.clientId = this.portalSettings.clientId;
        this.environment = this.portalSettings.environment;
      } catch (e) {
        if (window.injectedUrl) {
          // if portal settings is failing then it's likely our user credential
          this.logout();
          this.router.push({ path: '/login' });
        }
      }

      if (window.injectedUrl) {
        this.validateSessionId();
        const jsonToken = localStorage.getItem('ngStorage-token');
        if (jsonToken) {
          const token = JSON.parse(jsonToken) as AuthToken;
          await this.setToken(token);
        }
      } else if (this.token) {
        try {
          await this.setToken(this.token);
        } catch (e) {
          // if fetching profiles/user is failing it's likely our credential
          if (window.injectedUrl) {
            // if portal settings is failing then it's likely our user credential
            this.logout();
            this.router.push({ path: '/login' });
          }
        }
      }
    },
    validateSessionId() {
      const jsonToken = localStorage.getItem('ngStorage-token');
      const token = jsonToken ? JSON.parse(jsonToken) : null;
      // sessionId should normally exist, but if not
      // (e.g. SSO user logged in before AuthServer - 2503.03.01 deployment)
      // we need to create it to be used for insights chat history session
      if (token && !token.sessionId) {
        token.sessionId ??= uuid();
        localStorage.setItem('ngStorage-token', JSON.stringify(token));
      }
    },
    async setToken(token: AuthToken) {
      this.token = token;
      this.token.access_token ??= token.token;

      this.contentProfiles = await getContentProfiles();

      try {
        this.currentUser = await getCurrentUser();
      } catch (e) {
        // this is a new api and not all portals will have it
        // so this is just reconstructing our user object
        // we actually only need the permissions (for now)
        console.error('Backwards compatibility fix for ' + e);

        // we need backwards compatibility to build this user
        const permissions = [];
        const roles = (this.token.portalRole || this.token.roles).split(',');
        const validRoles = this.portalSettings!.viewingProfanityCleanedFieldsAccessibleToRoles ?? [];
        if (validRoles.length == 0 || roles.some((role) => validRoles.includes(role))) {
          permissions.push(Permission.DataMasking_Read_ProfanityAndName, Permission.DataMasking_Read_Sensitive);
        }

        this.currentUser = {
          id: this.token.id,
          client: this.token.clientId,
          userName: this.token.userName,
          firstName: this.token.firstName,
          lastName: this.token.lastName,
          email: this.token.email,
          portalRoles: roles,
          permissions: permissions,
          // we don't use anything for this right now
          // so backwards compatibility wise we can just ignore
          claims: [],
        };
      }
    },
    trackUniqueEvent(name: string, properties: ICustomProperties) {
      if (!this.eventsSent) this.eventsSent = {};
      // if the event was sent more than 24 hrs ago then send out
      if (
        !this.eventsSent[name] ||
        Number(new Date(this.eventsSent[name])) < Date.now() - tau.hours(24).toValue(tau.TimeUnit.Milliseconds)
      ) {
        this.eventsSent[name] = Date.now().toString();
        trackAppInsightsEvent(name, properties);
      }
    },
    async versionCheck() {
      // Load version information
      await wrapFallible(async () => {
        const version = await getVersion();
        if (version.currentVersion != this.frontendVersion) {
          if (this.frontendVersion) {
            // force reload of page
            useToast().success('Refreshing due to a new version', {
              typeOfContent: 'Refresh',
              timeout: tau.seconds(2).milliseconds(),
              onClose: () => location.reload(),
            } as any);
          }
          this.frontendVersion = version.currentVersion;
        }
      });
    },
    // we need a proper localization system but for backwards compatibility this
    // is okay.
    legacyTranslationLookup(key: string) {
      return this.portalSettings?.languages[0].translations.find((t) => t.key.toLowerCase() == key.toLowerCase());
    },
    logout() {
      // nullify state
      this.token = null;
      this.environment = null;
      this.portalUri = null;
      this.clientId = null;
      this.portalSettings = null;
    },
    async apiKeyLogin(sessionId: string, apiKey: string) {
      const body = {
        grant_type: 'api_key',
        api_key: apiKey,
        registration_id: sessionId,
        client_id: this.clientId,
        environment: this.environment,
        application: this.appName,
      };

      const appInsights = useAppInsights();
      appInsights.setAuthenticatedUserContext(sessionId);

      const tok = await axios.post(`${this.authServerUri}/token`, formUrlEncodingSerialization(body), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      this.token = tok.data;

      // force version check after refresh of data
      await this.versionCheck();
    },
  },
  persist: {
    paths: ['token', 'environment', 'appName', 'clientId', 'portalUri', 'uniqueEvents', 'moduleId', 'contentProfile'],
    afterRestore: async (ctx) => {
      ctx.store.authServerUri = import.meta.env.VITE_BASE_AUTHSERVER_URL;
    },
  },
});
